import React, { createContext, useContext, useState, useEffect } from "react";
import { getTaxes } from "../services/tax.service";
import { getShippingCharges } from "../services/shipping.service";

const CartContext = createContext();

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        console.error("useCart must be used within a CartProvider");
    }
    return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
        const savedCart = localStorage.getItem("cart");
        return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
        console.error("Error loading cart from localStorage:", error);
        return [];
    }
  });

  const [taxes, setTaxes] = useState([]);
  const [shippingCharges, setShippingCharges] = useState([]);

  useEffect(() => {
    const fetchRates = async () => {
        try {
            const [taxData, shipData] = await Promise.all([
                getTaxes(true),
                getShippingCharges(true)
            ]);
            setTaxes(taxData);
            setShippingCharges(shipData);
        } catch (error) {
            console.error("Error fetching rates:", error);
        }
    };
    fetchRates();
  }, []);

  useEffect(() => {
    try {
        localStorage.setItem("cart", JSON.stringify(cart));
    } catch (error) {
        console.error("Error saving cart to localStorage:", error);
    }
  }, [cart]);

  const addToCart = (product, variant = null, quantity = 1) => {
    console.log("Adding to cart:", product.name, variant ? `(Variant: ${variant.value})` : "");
    
    setCart((prevCart) => {
      const existingItemIndex = prevCart.findIndex(
        (item) => item.id === product.id && item.variantId === (variant?.id || null)
      );

      const maxStock = Number(variant?.stock ?? product?.stock ?? 999);

      if (existingItemIndex > -1) {
        const newCart = [...prevCart];
        const existingItem = newCart[existingItemIndex];
        
        if (existingItem.quantity + quantity > maxStock) {
            return prevCart;
        }
        
        newCart[existingItemIndex].quantity += quantity;
        return newCart;
      }

      if (quantity > maxStock) {
        return prevCart;
      }

      const newItem = {
        id: product.id,
        variantId: variant?.id || null,
        name: product.name,
        price: variant?.price || product.price || 0,
        actualPrice: variant?.actualPrice || product.actualPrice || 0,
        image: variant?.imageUrl || (product.imageUrls && product.imageUrls[0]) || "",
        quantity,
        stock: maxStock,
        variantName: variant?.value || null,
        taxId: product.taxId || null,
        shippingChargeId: product.shippingChargeId || null,
      };
      
      return [...prevCart, newItem];
    });
  };

  const removeFromCart = (productId, variantId = null) => {
    setCart((prevCart) =>
      prevCart.filter(
        (item) => !(item.id === productId && item.variantId === variantId)
      )
    );
  };

  const updateQuantity = (productId, variantId, quantity) => {
    setCart((prevCart) =>
      prevCart.map((item) => {
        if (item.id === productId && item.variantId === variantId) {
            const newQty = Math.max(1, quantity);
            if (item.stock !== undefined && item.stock !== null && newQty > item.stock) {
                alert(`Only ${item.stock} units available in stock.`);
                return item;
            }
            return { ...item, quantity: newQty };
        }
        return item;
      })
    );
  };

  const clearCart = () => setCart([]);

  // Calculations
  const cartSubtotal = cart.reduce((total, item) => total + item.price * item.quantity, 0);
  
  const taxCalculation = cart.reduce((acc, item) => {
    const tax = taxes.find(t => t.id === item.taxId);
    if (!tax) return acc;
    
    const itemSubtotal = item.price * item.quantity;
    if (tax.taxType === "INCLUSIVE") {
        const taxAmount = itemSubtotal - (itemSubtotal / (1 + tax.rate / 100));
        acc.inclusive += taxAmount;
    } else {
        const taxAmount = itemSubtotal * (tax.rate / 100);
        acc.exclusive += taxAmount;
    }
    return acc;
  }, { inclusive: 0, exclusive: 0 });

  const totalTax = taxCalculation.inclusive + taxCalculation.exclusive;
  const exclusiveTaxTotal = taxCalculation.exclusive;

  const totalShipping = 0; 

  const cartTotal = cartSubtotal + exclusiveTaxTotal + totalShipping;
  const cartCount = cart.reduce((count, item) => count + item.quantity, 0);

  const isInCart = (productId, variantId = null) => {
    return cart.some(item => item.id === productId && item.variantId === variantId);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartSubtotal,
        totalTax,
        taxCalculation,
        totalShipping,
        cartTotal,
        cartCount,
        isInCart,
        taxes,
        shippingCharges
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
